/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: TenantsPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the tenants module.
----------------------------------------------------------------*/

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useDataGrid } from '../../hooks/useDataGrid';
import { useNotifications } from '../../contexts/NotificationContext';
import { Building } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useAuthStore } from '../../store/auth';
import { ModulePageContainer } from '../../components/layouts';
import { deleteTenantById, navigateToTenantDuplicate } from './tenantsActions';
import { useTenantsList } from './hooks/useTenantsList';
import { useTenantsColumns } from './hooks/useTenantsColumns';
import { TenantsActions } from './components/TenantsActions';
import type { Tenant } from './types';

export default function TenantsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setCurrentPageTitle } = useNavigationStore();
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotifications();
  const tenantSlug = user?.tenant?.slug || '';

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Clear page title to show tenant logo + name in header
  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  // Hooks
  const { fetchTenants } = useTenantsList();
  const { availableColumns, getColumnRenderers } = useTenantsColumns();

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData: fetchTenants,
    initialColumns: availableColumns,
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 10,
    moduleName: 'tenants'
  });

  // Handle create tenant
  const handleCreateTenant = useCallback(() => {
    navigate('/admin/multi-tenant/tenants/new');
  }, [navigate]);

  // Handle edit tenant
  const handleEditTenant = useCallback((tenant: Tenant) => {
    navigate(`/admin/multi-tenant/tenants/${tenant.uuid ?? tenant.id}`);
  }, [navigate]);

  // Handle duplicate tenant
  const handleDuplicateTenant = useCallback((tenant: Tenant) => {
    navigateToTenantDuplicate(navigate, tenant.uuid ?? tenant.id!);
  }, [navigate]);

  // Handle delete tenant
  const handleDeleteTenant = useCallback((tenant: Tenant) => {
    setTenantToDelete(tenant);
    setShowDeleteModal(true);
  }, []);

  // Confirm delete
  const confirmDelete = useCallback(async () => {
    if (!tenantToDelete) return;

    try {
      setIsDeleting(true);
      await deleteTenantById(tenantToDelete.id!, tenantSlug, user?.id);
      showSuccess(t('tenants.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setTenantToDelete(null);
      refresh();
    } catch (error) {
      showError(error?.message || t('tenants.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [tenantToDelete, showSuccess, showError, t, refresh, tenantSlug, user?.id]);

  // Render actions
  const renderActions = useCallback((tenant: Tenant) => (
    <TenantsActions
      tenant={tenant}
      onEdit={handleEditTenant}
      onDuplicate={handleDuplicateTenant}
      onDelete={handleDeleteTenant}
    />
  ), [handleDeleteTenant, handleDuplicateTenant, handleEditTenant]);

  // If we're in form view, show the form
  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            titleIcon={<Building className="h-5 w-5 text-primary" />}
            columns={getColumnRenderers(columns)}
          onColumnToggle={handleColumnToggle}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onAdd={handleCreateTenant}
          onEdit={handleEditTenant}
          onDuplicate={handleDuplicateTenant}
          onDelete={handleDeleteTenant}
          title={t('tenants.title')}
          addButtonText={t('tenants.addTenant')}
          searchPlaceholder={t('tenants.searchTenants')}
          emptyMessage={t('tenants.noTenantsFound')}
          deleteTitle={t('tenants.deleteTenant')}
          deleteMessage={(item) => {
            if (!item || !item.name) {
              return t('tenants.deleteConfirmMessage');
            }
            return t('tenants.deleteConfirm', { name: item.name });
          }}
          renderActions={renderActions}
          translations={{
            items: t('common.items'),
            columns: t('common.columns'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll')
          }}
        />

          {/* Delete Confirmation Modal */}
          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title={t('tenants.deleteTenant')}
            message={tenantToDelete ? t('tenants.deleteConfirm', { name: tenantToDelete.name }) : ''}
            confirmText={t('common.delete')}
            cancelText={t('common.cancel')}
            isLoading={isDeleting}
            type="danger"
          />
        </>
      }
      formView={null}
    />
  );
}
